import { Button, PageHeader } from '@lmc/ui-kit'
import { Select, Space, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useState } from 'react'
import { useGetFunders } from '../../../Rostering/hooks/useFunders'
import {
  useExportBillingGaps,
  useGetBillingGaps
} from '../../hooks/useBillingGaps'
import type {
  BillingGapRow,
  BillingGapsFilters,
  ConfigStatus
} from '../../types/billingGaps'
import { GapType } from '../../types/billingGaps'

const GAP_TYPE_LABELS: Record<GapType, string> = {
  [GapType.NO_FUNDER]: 'No funder',
  [GapType.NO_CONTRACT]: 'No contract',
  [GapType.NO_INVOICING_RATE]: 'No invoicing rate'
}

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Hospitalised', value: 'hospitalised' },
  { label: 'Discharged', value: 'discharged' }
]

const GAP_TYPE_OPTIONS = Object.values(GapType).map((g) => ({
  label: GAP_TYPE_LABELS[g],
  value: g
}))

const configStatusCell = (status: ConfigStatus) => {
  switch (status) {
    case 'yes':
      return <Tag color="success">✓</Tag>
    case 'partial':
      return <Tag color="warning">Partial</Tag>
    case 'no':
      return <Tag color="error">✗</Tag>
    default:
      return <span style={{ color: '#999' }}>—</span>
  }
}

const COLUMNS: ColumnsType<BillingGapRow> = [
  {
    title: 'Resident',
    dataIndex: 'residentName',
    key: 'residentName',
    sorter: (a, b) => a.residentName.localeCompare(b.residentName)
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
  },
  {
    title: 'Funders',
    dataIndex: 'fundersCount',
    key: 'fundersCount',
    render: (n: number) =>
      n === 0 ? <span style={{ color: '#999' }}>None</span> : n
  },
  {
    title: 'Contract',
    dataIndex: 'contractStatus',
    key: 'contractStatus',
    render: configStatusCell
  },
  {
    title: 'Inv. Rate',
    dataIndex: 'invoicingRateStatus',
    key: 'invoicingRateStatus',
    render: configStatusCell
  },
  {
    title: 'Funded h/wk',
    dataIndex: 'fundedHours',
    key: 'fundedHours',
    render: (h: number | null) =>
      h != null ? `${h}h` : <span style={{ color: '#999' }}>—</span>,
    sorter: (a, b) => (a.fundedHours ?? 0) - (b.fundedHours ?? 0)
  },
  {
    title: 'Package h/wk',
    dataIndex: 'packageHours',
    key: 'packageHours',
    render: (h: number | null) =>
      h != null ? `${h}h` : <span style={{ color: '#999' }}>—</span>
  },
  {
    title: 'Missing',
    dataIndex: 'gaps',
    key: 'gaps',
    render: (gaps: GapType[]) =>
      gaps.length === 0 ? (
        <span style={{ color: '#999' }}>—</span>
      ) : (
        gaps.map((g) => GAP_TYPE_LABELS[g]).join(', ')
      ),
    sorter: (a, b) => b.gaps.length - a.gaps.length
  }
]

export const BillingGapsReport = () => {
  const [filters, setFilters] = useState<BillingGapsFilters>({
    statuses: ['active']
  })

  const { data = [], isLoading } = useGetBillingGaps(filters)
  const { mutate: exportCsv, isLoading: isExporting } = useExportBillingGaps()

  const { data: funders = [] } = useGetFunders()
  const funderOptions = funders.map((f) => ({ label: f.name, value: f._id }))

  return (
    <>
      <PageHeader title="Billing Setup Gaps">
        <Button
          variant="primary"
          onClick={() => exportCsv(filters)}
          isLoading={isExporting}
        >
          Export CSV
        </Button>
      </PageHeader>
      <div style={{ padding: '0 24px 24px' }}>
        <p style={{ color: '#666', marginBottom: 16 }}>
          Identify residents with incomplete invoicing configuration.
        </p>
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            mode="multiple"
            placeholder="Status"
            value={filters.statuses}
            onChange={(statuses) => setFilters((f) => ({ ...f, statuses }))}
            options={STATUS_OPTIONS}
            style={{ minWidth: 160 }}
            allowClear
          />
          <Select
            mode="multiple"
            placeholder="Gap type"
            value={filters.gapTypes}
            onChange={(gapTypes) => setFilters((f) => ({ ...f, gapTypes }))}
            options={GAP_TYPE_OPTIONS}
            style={{ minWidth: 180 }}
            allowClear
          />
          {funderOptions.length > 0 && (
            <Select
              placeholder="All funders"
              value={filters.funderId}
              onChange={(funderId) => setFilters((f) => ({ ...f, funderId }))}
              options={funderOptions}
              style={{ minWidth: 160 }}
              allowClear
            />
          )}
        </Space>
        <Table
          columns={COLUMNS}
          dataSource={data}
          rowKey="residentId"
          loading={isLoading}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          size="small"
        />
      </div>
    </>
  )
}

export default BillingGapsReport
